import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Link } from "wouter";

export default function BetaSignups() {
  const { user, loading } = useAuth();
  const { data: signups, isLoading, error } = trpc.beta.list.useQuery(undefined, {
    enabled: !!user && user.role === "admin",
  });

  if (loading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d0d0d" }}>
        <div className="text-sm tracking-widest uppercase" style={{ color: "#4a7c59" }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: "#0d0d0d" }}>
        <div className="text-lg font-bold" style={{ color: "#e8ede9" }}>
          Access Denied
        </div>
        <p className="text-sm" style={{ color: "#6b7a6e" }}>
          This page is restricted to admins.
        </p>
        <Link href="/" className="text-sm underline" style={{ color: "#4a7c59" }}>
          ← Back to App
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d0d0d" }}>
        <div className="text-sm" style={{ color: "#e87070" }}>
          Failed to load signups: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ background: "#0d0d0d" }}>
      {/* Top rule */}
      <div className="w-full h-px" style={{ background: "linear-gradient(90deg, transparent, #4a7c59, transparent)" }} />

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Link href="/" className="text-xs tracking-widest uppercase hover:opacity-80" style={{ color: "#4a7c59" }}>
                ← App
              </Link>
              <span style={{ color: "#3a4a3e" }}>/</span>
              <span className="text-xs tracking-widest uppercase" style={{ color: "#6b7a6e" }}>
                Beta Signups
              </span>
            </div>
            <h1 className="text-2xl font-bold tracking-wide" style={{ color: "#e8ede9" }}>
              Beta Signups
            </h1>
          </div>
          <div
            className="px-3 py-1 text-xs font-bold tracking-widest uppercase border"
            style={{ color: "#4a7c59", borderColor: "#4a7c59", background: "rgba(74,124,89,0.08)" }}
          >
            {signups?.length ?? 0} Total
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px" style={{ background: "rgba(74,124,89,0.2)" }} />
          <span className="text-xs tracking-widest uppercase" style={{ color: "#3a4a3e" }}>
            FORGE // TELEMETRY — BETA LIST
          </span>
          <div className="flex-1 h-px" style={{ background: "rgba(74,124,89,0.2)" }} />
        </div>

        {/* Table */}
        {!signups || signups.length === 0 ? (
          <div
            className="text-center py-16 border"
            style={{ borderColor: "rgba(74,124,89,0.2)", color: "#3a4a3e" }}
          >
            <div className="text-3xl mb-4">⚒️</div>
            <p className="text-sm tracking-widest uppercase">No signups yet</p>
            <p className="text-xs mt-2" style={{ color: "#2a3a2e" }}>
              Share the beta link to start collecting applications.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(74,124,89,0.25)" }}>
                  {["#", "Name", "Email", "Gym", "Signed Up"].map((h) => (
                    <th
                      key={h}
                      className="text-left py-3 px-4 text-xs font-bold tracking-widest uppercase"
                      style={{ color: "#4a7c59" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {signups.map((s, i) => (
                  <tr
                    key={s.id}
                    style={{
                      borderBottom: "1px solid rgba(74,124,89,0.1)",
                      background: i % 2 === 0 ? "transparent" : "rgba(74,124,89,0.02)",
                    }}
                  >
                    <td className="py-3 px-4" style={{ color: "#3a4a3e" }}>
                      {signups.length - i}
                    </td>
                    <td className="py-3 px-4 font-medium" style={{ color: "#e8ede9" }}>
                      {s.name}
                    </td>
                    <td className="py-3 px-4" style={{ color: "#a3b8a8" }}>
                      <a href={`mailto:${s.email}`} className="hover:underline">
                        {s.email}
                      </a>
                    </td>
                    <td className="py-3 px-4" style={{ color: "#6b7a6e" }}>
                      {s.gymName ?? <span style={{ color: "#3a4a3e" }}>—</span>}
                    </td>
                    <td className="py-3 px-4 text-xs" style={{ color: "#4a5a4e" }}>
                      {new Date(s.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Beta link callout */}
        <div
          className="mt-10 p-4 border text-sm"
          style={{ borderColor: "rgba(74,124,89,0.2)", background: "rgba(74,124,89,0.04)" }}
        >
          <span className="text-xs tracking-widest uppercase mr-3" style={{ color: "#4a7c59" }}>
            Beta URL
          </span>
          <span style={{ color: "#6b7a6e" }}>
            {window.location.origin}/beta
          </span>
        </div>
      </div>
    </div>
  );
}
